import { Catalog, SingleSourceOfTruth } from '@philips-emr/tws-core';
import ComponentAPI, { configure } from '@philips-emr/tws-core/lib/component-api';
import { ComponentClass } from '@philips-emr/tws-core/lib/registry/component-info';

import PropsProcessor from '../bridge/props-processor';
import { Node } from '../metadata';
import { resolveProps } from '../util';

import EngineEventDelegator from './engine-event-delegator';
import FragmentNode from './fragment-node';

interface ComponentArgs {
  /** The instance uuid. */
  instanceUUID: string;
  /** The node metadata. */
  nodeMetadata: Node;
  /** The owner instance. */
  owner: FragmentNode;
  /** The node uuid. */
  uuid: string;
}

/**
 * Component node presentation.
 */
class ComponentNode {

  private api: ComponentAPI;
  private eventDelegator: EngineEventDelegator;

  private nodeMetadata: Node;

  private owner: FragmentNode;
  private propsProcessor: PropsProcessor;
  private ssot: SingleSourceOfTruth;
  
  private type: string;
  private uuid: string;

  constructor(args: ComponentArgs) {
    const { nodeMetadata, owner, uuid } = args;

    this.uuid = uuid;
    this.owner = owner;
    this.nodeMetadata = nodeMetadata;

    const { type, props } = nodeMetadata;
    
    this.type = type;

    const componentData = Catalog.get(type);
    const component = componentData.getComponent();

    const { defaultProps = {}, propTypes = {} } = component;

    // TODO: it might have a better way to specify engine props (Ex.: alias)
    const propsDefinition = [...Object.keys(propTypes), 'alias'];
    
    this.ssot = new SingleSourceOfTruth(this.getHashCode(), defaultProps, propsDefinition);
    this.ssot.setMetaProps(resolveProps(props));

    this.eventDelegator = new EngineEventDelegator(this);

    const API = componentData.getAPI() || ComponentAPI;
    this.api = new API();
    configure(this.api, this.ssot, this.eventDelegator);

    this.propsProcessor = new PropsProcessor(this, this.owner);

  }

  /**
   * Retrieves the node API instance.
   * @returns the node API instance.
   */
  getAPI(): ComponentAPI {
    return this.api;
  }

  /**
   * Retrieves the node's instance of EventDelegator.
   * @returns the instance of EventDelegator.
   */
  getEventDelegator(): EngineEventDelegator {
    return this.eventDelegator;
  }

  /**
   * Retrieves the instance hash-code.
   * @returns the instance hash-code.
   */
  getHashCode(): string {
    const parentHashCode = this.owner.getHashCode();
    return `${parentHashCode}_${this.uuid}`;
  }

  /**
   * Retrieves the node metadata.
   * @returns the node metadata.
   */
  getNodeMetadata(): Node {
    return this.nodeMetadata;
  }

  /**
   * Retrieves the node owner.
   * @returns the node owner.
   */
  getOwner(): FragmentNode {
    return this.owner;
  }

  /**
   * Retrieves the node props-processor.
   * @returns the node props-processor.
   */
  getPropsProcessor(): PropsProcessor {
    return this.propsProcessor;
  }

  /**
   * Retrieves the ComponentType that must be rendered.
   * @returns the ComponentType that must be rendered.
   */
  getRenderable(): ComponentClass {
    const data = Catalog.get(this.type);
    return data.getComponent();
  }

  /**
   * Retrieves the node instance of SingleSourceOfTruth.
   * @returns the instance of SingleSourceOfTruth.
   */
  getSSOT(): SingleSourceOfTruth {
    return this.ssot;
  }

  /**
   * Retrieves the node type as String.
   * @returns the node type as String.
   */
  getType(): string {
    return this.type;
  }

  /**
   * Retrieves the node uuid.
   * @returns the node uuid.
   */
  getUUID(): string {
    return this.uuid;
  }

}

export default ComponentNode;
